import type { MeetingTypeVariant } from '../../../utils/meetingAccessPolicy';
import type { CalendarColorOption, CalendarOption } from '../types';

export const SCHEDULE_MEETING_DRAFT_QUERY_KEY = ['schedule-meeting-modal', 'draft'];

/** Calendar color field value: omit color on the event (Google `colorId` / Outlook `categories`). */
export const SCHEDULE_MEETING_NO_COLOR_ID = '';

export const SCHEDULE_MEETING_NO_COLOR_OPTION: CalendarColorOption = {
  id: SCHEDULE_MEETING_NO_COLOR_ID,
  label: 'No color',
  hexColor: '#ffffff',
};

export const HIDE_LINK_TIMEOUT = 2000;

export const MEETING_ID_SELECTION_TITLE = 'Personal meeting';

/**
 * Dropdown value standing for "let the backend allocate a fresh room". Rooms are
 * always server-assigned, so this cannot be a real room id like the other options.
 */
export const GENERATE_UNIQUE_MEETING_ID = '__generate-unique-meeting-id__';

export const GENERATE_UNIQUE_MEETING_ID_LABEL = 'Generate a unique meeting ID';

/** Closed-list label for the profile default personal meeting. */
export const DEFAULT_MEETING_OPTION_LABEL = 'Default meeting ID';

export const CALENDAR_TYPE_OUTLOOK: CalendarOption = 'outlook';
export const CALENDAR_TYPE_GOOGLE: CalendarOption = 'google';
export const CALENDAR_TYPE_OTHER: CalendarOption = 'other';

export const CALENDAR_OPTIONS: { id: CalendarOption; label: string }[] = [
  { id: CALENDAR_TYPE_OUTLOOK, label: 'Outlook' },
  { id: CALENDAR_TYPE_GOOGLE, label: 'Google calendar' },
  { id: CALENDAR_TYPE_OTHER, label: 'Other calendars' },
];

/** Shown in Schedule meeting: Microsoft only if connected, Google only if connected, Other always. */
export const getCalendarOptionsForAccount = (
  googleConnected: boolean,
  microsoftConnected: boolean,
): { id: CalendarOption; label: string }[] => {
  const options: { id: CalendarOption; label: string }[] = [];
  if (microsoftConnected) {
    options.push({ id: CALENDAR_TYPE_OUTLOOK, label: 'Outlook' });
  }
  if (googleConnected) {
    options.push({ id: CALENDAR_TYPE_GOOGLE, label: 'Google calendar' });
  }
  options.push({ id: CALENDAR_TYPE_OTHER, label: 'Other calendars' });
  return options;
};

export const getDefaultCalendarOptionForAccount = (
  googleConnected: boolean,
  microsoftConnected: boolean,
): CalendarOption => {
  if (microsoftConnected) {
    return CALENDAR_TYPE_OUTLOOK;
  }
  if (googleConnected) {
    return CALENDAR_TYPE_GOOGLE;
  }
  return CALENDAR_TYPE_OTHER;
};

/** Access policy a room gets when there is none to derive: Trusted Meetings. */
export const DEFAULT_MEETING_TYPE: MeetingTypeVariant = 'standard';

export const DEFAULT_DURATION = 60; // in minutes
export const MIN_DURATION = 15;
export const MAX_DURATION = 480;
export const MILLISECONDS_IN_MINUTE = 60000;
